/* eslint react-hooks/exhaustive-deps: "warn" */

import { type CSSProperties, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Flag } from 'ui/atoms/Flag/Flag';
import { Button } from 'ui/atoms/Button/Button';
import { Section } from 'ui/molecules/Section/Section';

import styles from './LinkageTableSection.module.scss';

type Row = {
  branch: string;
  name: string;
  level: string;
  flag: string;
  country: string;
  regn: string;
  hidden?: boolean;
};

const ROWS: Row[] = [
  { branch: '├─ ', name: 'NVIDIA Graphics Holding Company', level: 'L1', flag: 'mu', country: 'MU', regn: '51232' },
  {
    branch: '│  └─ ',
    name: 'NVIDIA Graphics Private Limited',
    level: 'L2',
    flag: 'in',
    country: 'IN',
    regn: '29AABCN9200H1ZI',
  },
  {
    branch: '├─ ',
    name: 'NVIDIA International Holdings Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 3133415',
  },
  {
    branch: '│  └─ ',
    name: 'Mellanox Technologies, Ltd.',
    level: 'L2',
    flag: 'il',
    country: 'IL',
    regn: '512763285',
  },
  {
    branch: '│     ├─ ',
    name: 'Mellanox Technologies UK Ltd',
    level: 'L3',
    flag: 'gb',
    country: 'GB',
    regn: '06251613',
  },
  {
    branch: '│     │  ├─ ',
    name: 'NVIDIA Denmark ApS',
    level: 'L4',
    flag: 'dk',
    country: 'DK',
    regn: '27373968',
  },
  {
    branch: '│     │  │  └─ ',
    name: 'Iptronics, Inc.',
    level: 'L5',
    flag: 'us',
    country: 'US',
    regn: 'CA 4440501',
  },
  {
    branch: '│     │  ├─ ',
    name: 'Mellanoks Tekhnolodzhiz Ukraina TOV',
    level: 'L4',
    flag: 'ua',
    country: 'UA',
    regn: '42197229',
  },
  {
    branch: '│     │  ├─ ',
    name: 'Mellanox Technologies UK Holdings Ltd',
    level: 'L4',
    flag: 'gb',
    country: 'GB',
    regn: '12362293',
  },
  {
    branch: '│     │  └─ ',
    name: 'NVIDIA Belfast Ltd.',
    level: 'L4',
    flag: 'gb',
    country: 'GB',
    regn: 'NI065218',
  },
  {
    branch: '│     ├─ ',
    name: 'Mellanox Technologies, Inc.',
    level: 'L3',
    flag: 'us',
    country: 'US',
    regn: 'CA 2155872',
    hidden: true,
  },
  {
    branch: '│     │  └─ ',
    name: 'Mellanox Federal Systems, LLC',
    level: 'L4',
    flag: 'us',
    country: 'US',
    regn: '#0189761717',
    hidden: true,
  },
  {
    branch: '│     ├─ ',
    name: 'Cigol Digital Systems Ltd',
    level: 'L3',
    flag: 'il',
    country: 'IL',
    regn: '514356765',
    hidden: true,
  },
  {
    branch: '│     └─ ',
    name: 'Mellanox Technologies Distribution Ltd',
    level: 'L3',
    flag: 'il',
    country: 'IL',
    regn: '514595412',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'NVIDIA International, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 6004882',
    hidden: true,
  },
  {
    branch: '│  ├─ ',
    name: 'Bright Computing Holding B.V.',
    level: 'L2',
    flag: 'nl',
    country: 'NL',
    regn: '53706870',
    hidden: true,
  },
  {
    branch: '│  │  └─ ',
    name: 'Bright Computing B.V.',
    level: 'L3',
    flag: 'nl',
    country: 'NL',
    regn: '34376066',
    hidden: true,
  },
  {
    branch: '│  ├─ ',
    name: 'NVIDIA Hong Kong Holdings Limited',
    level: 'L2',
    flag: 'hk',
    country: 'HK',
    regn: '33537210',
    hidden: true,
  },
  {
    branch: '│  │  └─ ',
    name: 'NVIDIA Hong Kong Holdings Ltd., Taiwan Branch',
    level: 'L3',
    flag: 'tw',
    country: 'TW',
    regn: '80022300',
    hidden: true,
  },
  {
    branch: '│  ├─ ',
    name: 'NVIDIA Singapore Development Pte. Ltd.',
    level: 'L2',
    flag: 'sg',
    country: 'SG',
    regn: '200804724D',
    hidden: true,
  },
  {
    branch: '│  │  ├─ ',
    name: 'NVIDIA Semi-conductor Technology (Beijing) Co., Ltd.',
    level: 'L3',
    flag: 'cn',
    country: 'CN',
    regn: '110000450394470',
    hidden: true,
  },
  {
    branch: '│  │  ├─ ',
    name: 'NVIDIA Singapore Development Pte. Ltd. Taiwan Branch',
    level: 'L3',
    flag: 'tw',
    country: 'TW',
    regn: '91055720',
    hidden: true,
  },
  {
    branch: '│  │  └─ ',
    name: 'NVIDIA Technical Service (Beijing) Co., Ltd .',
    level: 'L3',
    flag: 'cn',
    country: 'CN',
    regn: '110000450346744',
    hidden: true,
  },
  {
    branch: '│  ├─ ',
    name: 'NVIDIA Singapore Pte Ltd',
    level: 'L2',
    flag: 'sg',
    country: 'SG',
    regn: '200003831M',
    hidden: true,
  },
  {
    branch: '│  │  ├─ ',
    name: 'NVIDIA Singapore Pte Ltd',
    level: 'L3',
    flag: 'hk',
    country: 'HK',
    regn: '34234356',
    hidden: true,
  },
  {
    branch: '│  │  │  └─ ',
    name: 'NVIDIA G.K.',
    level: 'L4',
    flag: 'jp',
    country: 'JP',
    regn: '3010403005518',
    hidden: true,
  },
  {
    branch: '│  ├─ ',
    name: 'NVIDIA Computing Spain Sl.',
    level: 'L2',
    flag: 'es',
    country: 'ES',
    regn: 'B56726466',
    hidden: true,
  },
  {
    branch: '│  ├─ ',
    name: 'NVIDIA Hong Kong Development Limited',
    level: 'L2',
    flag: 'hk',
    country: 'HK',
    regn: '35495444',
    hidden: true,
  },
  {
    branch: '│  └─ ',
    name: 'NVIDIA New Zealand Limited',
    level: 'L2',
    flag: 'nz',
    country: 'NZ',
    regn: '8281780',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'NVIDIA International, Inc.',
    level: 'L1',
    flag: 'bb',
    country: 'BB',
    regn: '18482',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'NVIDIA Ltd',
    level: 'L1',
    flag: 'gb',
    country: 'GB',
    regn: '03900676',
    hidden: true,
  },
  {
    branch: '│  ├─ ',
    name: 'NVIDIA GmbH',
    level: 'L2',
    flag: 'de',
    country: 'DE',
    regn: '52070B8361',
    hidden: true,
  },
  {
    branch: '│  │  └─ ',
    name: 'NVIDIA Arc GmbH',
    level: 'L3',
    flag: 'de',
    country: 'DE',
    regn: '10623B25325B',
    hidden: true,
  },
  {
    branch: '│  │     └─ ',
    name: 'NVIDIA Arc GmbH Tyskland Sverige Filial',
    level: 'L4',
    flag: 'se',
    country: 'SE',
    regn: '5164049578',
    hidden: true,
  },
  {
    branch: '│  ├─ ',
    name: 'NVIDIA Ltd',
    level: 'L2',
    flag: 'be',
    country: 'BE',
    regn: 'BE0789902078',
    hidden: true,
  },
  {
    branch: '│  │  ├─ ',
    name: 'NVIDIA Ltd',
    level: 'L3',
    flag: 'es',
    country: 'ES',
    regn: 'N0069374G',
    hidden: true,
  },
  {
    branch: '│  │  │  ├─ ',
    name: 'NVIDIA Bulgaria Eood',
    level: 'L4',
    flag: 'bg',
    country: 'BG',
    regn: '206157462',
    hidden: true,
  },
  {
    branch: '│  │  │  ├─ ',
    name: 'NVIDIA Dutch B.V.',
    level: 'L4',
    flag: 'nl',
    country: 'NL',
    regn: '57653232',
    hidden: true,
  },
  {
    branch: '│  │  │  ├─ ',
    name: 'NVIDIA Italy SRL',
    level: 'L4',
    flag: 'it',
    country: 'IT',
    regn: 'IT07720340962',
    hidden: true,
  },
  {
    branch: '│  │  │  ├─ ',
    name: 'NVIDIA Poland Sp Z O O',
    level: 'L4',
    flag: 'pl',
    country: 'PL',
    regn: '5252658115',
    hidden: true,
  },
  {
    branch: '│  │  │  └─ ',
    name: 'NVIDIA Technologies Belgium',
    level: 'L4',
    flag: 'be',
    country: 'BE',
    regn: 'BE0798298617',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Oski Technology, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 2805179',
    hidden: true,
  },
  {
    branch: '│  └─ ',
    name: 'Oski Technology Private Limited',
    level: 'L2',
    flag: 'in',
    country: 'IN',
    regn: 'AAACC7186N',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Bright Computing, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 4736927',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Cumulus Networks, Inc',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0229361919',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Cumulus Networks, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 5090979',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Deepmap Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0155244398',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Ezchip Semiconductor, Inc',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0190808148',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Icera LLC',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 201003610128',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Icera Semiconductor LLC',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0229471900',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Icera, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0918898527',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Jah Venture Holdings, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0228423534',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Lpn Facilitator LLC',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0228387428',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Mellanox Technologies Silicon Photonics Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 1751446',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Mellanox Technologies Silicon Photonics, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0229474055',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Normerica International Corp.',
    level: 'L1',
    flag: 'bb',
    country: 'BB',
    regn: '39397',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'NVIDIA Development Inc',
    level: 'L1',
    flag: 'ca',
    country: 'CA',
    regn: '#0314756399',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'NVIDIA Development, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0190752528',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'NVIDIA Developpement France SAS',
    level: 'L1',
    flag: 'fr',
    country: 'FR',
    regn: '47997345500047',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'NVIDIA Switzerland AG',
    level: 'L1',
    flag: 'ch',
    country: 'CH',
    regn: 'CHE-109.505.206',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'NVIDIA US Investment Company',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: '#0129518188',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'NVIDIA, Helsinki OY',
    level: 'L1',
    flag: 'fi',
    country: 'FI',
    regn: '19792079',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Octoml, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 7529384',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Omniml, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 5272794',
    hidden: true,
  },
  {
    branch: '├─ ',
    name: 'Swiftstack, Inc.',
    level: 'L1',
    flag: 'us',
    country: 'US',
    regn: 'CA 5066025',
    hidden: true,
  },
  {
    branch: '└─ ',
    name: 'Vinbrain Company Limited',
    level: 'L1',
    flag: 'vn',
    country: 'VN',
    regn: '0108716920',
    hidden: true,
  },
];

const TOTAL_COUNT = ROWS.length + 1;

const hashStr = (s: string) => {
  let h = 0;

  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }

  return h;
};

const OWNERSHIP_OPTIONS = [100, 100, 100, 100, 100, 90, 80, 75, 60, 51];
const getOwnership = (regn: string) => `${OWNERSHIP_OPTIONS[hashStr(regn) % OWNERSHIP_OPTIONS.length]}%`;
const getIsDormant = (regn: string) => hashStr(regn) % 13 === 0;

export const LinkageTableSection = () => {
  const [expanded, setExpanded] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelRevealed = useIntersection(panelRef, { threshold: 0.1 });
  const { t } = useInt();

  return (
    <Section className={styles.root}>
      <div className={cn(styles.panel, panelRevealed && styles.panelIn)} ref={panelRef}>
        <div className={styles.panelHead}>
          <span className={styles.panelLabel}>{t('Corporate Linkage')}</span>
          <span className={styles.panelMeta}>NVIDIA Corporation · {t('Global Ultimate view')}</span>
        </div>

        <div className={cn(styles.scroll, expanded && styles.scrollExpanded)}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('Entity')}</th>
                <th>{t('Level')}</th>
                <th>{t('Country')}</th>
                <th>{t('Reg nr')}</th>
                <th>{t('Ownership')}</th>
                <th>{t('Status')}</th>
              </tr>
            </thead>

            <tbody>
              <tr className={styles.rowRoot}>
                <td className={styles.entity}>
                  NVIDIA Corporation
                  <span className={styles.guTag}>{t('GLOBAL ULTIMATE')}</span>
                </td>
                <td>
                  <span className={cn(styles.levelPill, styles.levelPillGU)}>GU</span>
                </td>
                <td>
                  <span className={styles.iso}>
                    <Flag code="us" className={styles.isoFlag} />
                    US
                  </span>
                </td>
                <td className={styles.regn}>CA 2862596</td>
                <td className={styles.ownership}>—</td>
                <td>
                  <span className={styles.statusPill}>
                    <span className={styles.statusDot} />
                    {t('Active')}
                  </span>
                </td>
              </tr>

              {ROWS.filter((row) => expanded || !row.hidden).map((row, i) => (
                <tr key={i} style={{ '--i': Math.min(i, 12) } as CSSProperties}>
                  <td className={styles.entity}>
                    <span className={styles.branch}>{row.branch}</span>
                    <span className={cn(styles.depthDot, styles[`depthDot${row.level}`])} />
                    {row.name}
                  </td>
                  <td>
                    <span className={cn(styles.levelPill, styles[`levelPill${row.level}`])}>{row.level}</span>
                  </td>
                  <td>
                    <span className={styles.iso}>
                      <Flag code={row.flag} className={styles.isoFlag} />
                      {row.country}
                    </span>
                  </td>
                  <td className={styles.regn}>{row.regn}</td>
                  <td className={styles.ownership}>{getOwnership(row.regn)}</td>
                  <td>
                    {getIsDormant(row.regn) ? (
                      <span className={cn(styles.statusPill, styles.statusPillDormant)}>
                        <span className={cn(styles.statusDot, styles.statusDotDormant)} />
                        {t('Dormant')}
                      </span>
                    ) : (
                      <span className={styles.statusPill}>
                        <span className={styles.statusDot} />
                        {t('Active')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.more}>
          <Button type="blue" size="md" htmlType="button" onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? t('Show fewer') : `${t('Show all')} ${TOTAL_COUNT} ${t('entities · 27 countries')}`}
          </Button>
        </div>
      </div>
    </Section>
  );
};
